import React, { useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, ArrowLeftRight, CalendarOff, Info, Clock } from "lucide-react";
import { TextField, TextArea, Toggle, HoursTable, DAYS } from "./shared";
import SurchargesSection from "./SurchargesSection";

const defaultHours = () => DAYS.reduce((acc, [key]) => ({ ...acc, [key]: { open: "09:00", close: "18:00", closed: false } }), {});

export const DEFAULT_LOCATION_SETTINGS = {
  hours_enabled: false,
  hours: null,
  min_duration_days: "",
  max_duration_days: "",
  min_lead_hours: "",
  max_advance_days: "",
  prep_buffer_hours: "",
  one_way: { enabled: false, fee: "", lock_next_departure: false },
  closures: [],
  access_info: "",
};

function GroupTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="h-3.5 w-3.5 text-slate-500" />}
      <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{children}</div>
    </div>
  );
}

export default function LocationSettingsPanel({ location, onSaved }) {
  const [s, setS] = useState(() => {
    const base = JSON.parse(JSON.stringify(DEFAULT_LOCATION_SETTINGS));
    const saved = location.settings || {};
    return {
      ...base,
      ...saved,
      one_way: { ...base.one_way, ...(saved.one_way || {}) },
      closures: saved.closures || [],
    };
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setSub = (group, k, v) => setS((p) => ({ ...p, [group]: { ...p[group], [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/vendor/locations/${location.id}`, { settings: s });
      toast.success(`Paramètres de « ${location.name} » enregistrés`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  const closures = s.closures || [];
  const addClosure = () => set("closures", [...closures, { id: `c-${Date.now()}`, label: "Fermeture", start: "", end: "" }]);
  const updateClosure = (id, patch) => set("closures", closures.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const removeClosure = (id) => set("closures", closures.filter((c) => c.id !== id));

  return (
    <div className="mt-4 pt-4 border-t border-slate-100 space-y-6" data-testid={`location-settings-${location.id}`}>
      {/* Horaires */}
      <div>
        <GroupTitle icon={Clock}>Horaires de l'agence</GroupTitle>
        <Toggle
          label="Horaires spécifiques à cette agence"
          hint="Sinon, l'agence suit les horaires globaux (Réservations & Flotte → Règles & horaires)."
          checked={!!s.hours_enabled}
          onChange={(v) => setS((p) => ({ ...p, hours_enabled: v, hours: p.hours || defaultHours() }))}
          testid={`loc-hours-toggle-${location.id}`}
        />
        {s.hours_enabled && (
          <div className="mt-3">
            <HoursTable value={s.hours || defaultHours()} onChange={(v) => set("hours", v)} testid={`loc-hours-${location.id}`} />
          </div>
        )}
      </div>

      {/* Règles de location */}
      <div>
        <GroupTitle>Règles de location de l'agence</GroupTitle>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <TextField label="Durée minimale" type="number" value={s.min_duration_days} onChange={(v) => set("min_duration_days", v)} suffix="jour(s)" placeholder="Global" hint="Vide = règle globale." testid={`loc-min-duration-${location.id}`} />
          <TextField label="Durée maximale" type="number" value={s.max_duration_days} onChange={(v) => set("max_duration_days", v)} suffix="jour(s)" placeholder="Global" hint="Vide = règle globale." testid={`loc-max-duration-${location.id}`} />
          <TextField label="Délai minimum avant départ" type="number" value={s.min_lead_hours} onChange={(v) => set("min_lead_hours", v)} suffix="heure(s)" placeholder="Aucun" hint="Ex. 12 = réservable au plus tard 12h avant." testid={`loc-min-lead-${location.id}`} />
          <TextField label="Délai maximal avant départ" type="number" value={s.max_advance_days} onChange={(v) => set("max_advance_days", v)} suffix="jour(s)" placeholder="Illimité" hint="Ex. 180 = réservable jusqu'à 6 mois à l'avance." testid={`loc-max-advance-${location.id}`} />
          <TextField label="Préparation entre 2 locations" type="number" value={s.prep_buffer_hours} onChange={(v) => set("prep_buffer_hours", v)} suffix="heure(s)" placeholder="Global" hint="Nettoyage, inspection. Vide = buffer global." testid={`loc-buffer-${location.id}`} />
        </div>
      </div>

      {/* Options & services */}
      <div>
        <GroupTitle>Options & services</GroupTitle>
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <Toggle label="Retour dans une autre agence (aller simple)" hint="Le client peut rendre le véhicule ailleurs, avec frais d'abandon." checked={!!s.one_way.enabled} onChange={(v) => setSub("one_way", "enabled", v)} testid={`loc-oneway-toggle-${location.id}`} />
            {s.one_way.enabled && (
              <div className="mt-3 pl-1 flex items-center gap-2">
                <ArrowLeftRight className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="relative w-40">
                  <input type="number" min="0" value={s.one_way.fee} onChange={(e) => setSub("one_way", "fee", e.target.value === "" ? "" : Number(e.target.value))} placeholder="Frais d'abandon" className="w-full h-9 px-3 pr-8 rounded-lg border border-slate-200 text-sm" data-testid={`loc-oneway-fee-${location.id}`} />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-semibold">€</span>
                </div>
              </div>
            )}
            {s.one_way.enabled && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <Toggle
                  label="Prochain départ uniquement depuis l'agence de restitution"
                  hint="Après restitution, le véhicule ne peut être réservé au départ que depuis l'agence où il se trouve, jusqu'à son prochain transfert confirmé."
                  checked={!!s.one_way.lock_next_departure}
                  onChange={(v) => setSub("one_way", "lock_next_departure", v)}
                  testid={`loc-oneway-lock-${location.id}`}
                />
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3">
            <SurchargesSection location={location} />
          </div>
        </div>
      </div>

      {/* Fermetures exceptionnelles */}
      <div>
        <GroupTitle icon={CalendarOff}>Fermetures exceptionnelles</GroupTitle>
        {closures.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 py-4 text-center text-xs text-slate-400">Aucune fermeture prévue (jours fériés, congés…).</div>
        ) : (
          <div className="space-y-2">
            {closures.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-2 p-2 rounded-xl border border-slate-200 bg-slate-50" data-testid={`loc-closure-${c.id}`}>
                <input value={c.label} onChange={(e) => updateClosure(c.id, { label: e.target.value })} placeholder="Motif (ex : Noël)" className="flex-1 min-w-[120px] h-9 px-2 rounded-md border border-slate-200 bg-white text-sm" />
                <input type="date" value={c.start} onChange={(e) => updateClosure(c.id, { start: e.target.value })} className="h-9 px-2 rounded-md border border-slate-200 bg-white text-xs" />
                <span className="text-xs text-slate-400">→</span>
                <input type="date" value={c.end} onChange={(e) => updateClosure(c.id, { end: e.target.value })} className="h-9 px-2 rounded-md border border-slate-200 bg-white text-xs" />
                <button type="button" onClick={() => removeClosure(c.id)} className="h-8 w-8 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 flex items-center justify-center">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <button type="button" onClick={addClosure} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 hover:text-violet-900 transition-colors" data-testid={`loc-add-closure-${location.id}`}>
          <Plus className="h-3.5 w-3.5" /> Ajouter une fermeture
        </button>
      </div>

      {/* Infos pratiques */}
      <div>
        <GroupTitle icon={Info}>Infos pratiques pour le client</GroupTitle>
        <TextArea
          label="Instructions d'accès"
          value={s.access_info}
          onChange={(v) => set("access_info", v)}
          rows={3}
          hint="Parking, étage, code portail… Affiché sur la confirmation de réservation."
          testid={`loc-access-info-${location.id}`}
        />
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 transition-colors"
          data-testid={`loc-settings-save-${location.id}`}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer les paramètres
        </button>
      </div>
    </div>
  );
}
